/* eslint-disable */
import React, { useEffect, useRef, useState } from 'react';
import { useUpdateMenu, useUpdateMenuThumbnail } from 'hooks';
import axios from 'axios';
import { Button, Col, Dropdown, MenuProps, Modal, Space } from 'antd';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { MenuButtonGroup, MenuCardWrapper, MenuStatusButtonGroup, MenuThumbnail, StyledDropdownButton } from './Style';
import { IMenuItem, IPresignedUrlResponse, menuStatusItems, TImageData } from './MenuManageSchema';
import * as logsUtils from 'utils/logsUtils';
import { getImageUploadError, getUploadContentType, UPLOAD_IMAGE_ACCEPT } from 'utils';

const getMenuStatusColor = (status: string) => {
  if (status === 'ACTIVE') {
    return '#47ae66';
  }
  if (status === 'SOLD_OUT') {
    return '#f0ad4e';
  }
  return '#d9534f';
};

const MenuCard = ({
  storeId,
  menuData,
  refetchMenuLists,
  handleOpenModal,
  getPreSignedUrl
}: {
  storeId: string;
  menuData: IMenuItem;
  refetchMenuLists: () => void;
  handleOpenModal: () => void;
  getPreSignedUrl: (file: File) => Promise<IPresignedUrlResponse>;
}) => {
  const { id, name, imageThumbnail, status, intro }: IMenuItem = menuData;
  const inputRef = useRef<HTMLInputElement>(null);
  const [imageInfo, setImageInfo] = useState<TImageData>({ file: null, previewURL: imageThumbnail || '' });
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id
  });
  const [menuStatus, setMenuStatus] = useState<{ status: string; backgroundColor: string; borderColor: string }>({
    status,
    backgroundColor: getMenuStatusColor(status),
    borderColor: getMenuStatusColor(status)
  });

  const { mutateAsync: updateMenuStatusAsync } = useUpdateMenu();
  const { mutateAsync: updateMenuThumbnailAsync } = useUpdateMenuThumbnail();

  useEffect(() => {
    setImageInfo({ file: null, previewURL: imageThumbnail || '' });
  }, [imageThumbnail]);

  useEffect(() => {
    setMenuStatus({
      status,
      backgroundColor: getMenuStatusColor(status),
      borderColor: getMenuStatusColor(status)
    });
  }, [status]);

  const updateMenuStatus = async (statusKey: string) => {
    await updateMenuStatusAsync({ sid: storeId, data: { menuIds: [id], status: statusKey } });
    await refetchMenuLists();
  };

  const getMenuStatusLabel = (statusKey: string) => {
    const item = menuStatusItems.find((menuItem) => menuItem.value === statusKey);
    return item && 'label' in item ? item.label : statusKey;
  };

  const handleMenuStatusChange = async (handleMenuStatus: string) => {
    const backgroundColor = getMenuStatusColor(handleMenuStatus);
    const borderColor = getMenuStatusColor(handleMenuStatus);
    setMenuStatus({ status: handleMenuStatus, backgroundColor, borderColor });
    await updateMenuStatus(handleMenuStatus);
  };

  const handleStatusDropdownClick = (event: React.MouseEvent<Element>) => event.stopPropagation();

  const statusMenuItems: MenuProps['items'] = menuStatusItems.map((item) => {
    return {
      label: item.label,
      key: item.value,
      onClick: () => handleMenuStatusChange(item.value as string)
    };
  });

  const imageUploadOnClick = () => {
    inputRef?.current?.click();
  };

  const uploadFileToS3 = async (preSignedUrl: string, file: File): Promise<void> => {
    try {
      const response = await axios.put(preSignedUrl, file, {
        headers: { 'Content-Type': getUploadContentType(file.name) }
      });

      if (response.status !== 200) throw new Error('S3 업로드 실패');
    } catch (error) {
      const errorContext = {
        response: `${error?.response?.data}`,
        fileName: file.name,
        fileType: file.type,
        preSignedUrl: preSignedUrl?.split('?')[0],
        errorMessage: error instanceof Error ? error.message : 'Unknown error'
      };
      logsUtils.errorLogger(`[MENU-MANAGE-IMAGE-UPLOAD-ERROR]: ${error?.response?.data}`, { 'ui-args': errorContext });

      throw new Error('S3 업로드 실패');
    }
  };

  const handleImageUpload = async (file: File) => {
    const img = new Image();
    let imgWidth = 0;
    let imgHeight = 0;
    img.src = URL.createObjectURL(file);
    img.onload = () => {
      imgWidth = img.width;
      imgHeight = img.height;
    };

    if (file.type.includes('image')) {
      const validationError = getImageUploadError(file.name);
      if (validationError) {
        Modal.error({
          content: validationError
        });
        return;
      }

      const preSignedUrlResponse: IPresignedUrlResponse = await getPreSignedUrl(file);
      const { preSignedUrl, uploadedFileUrl } = preSignedUrlResponse;
      await uploadFileToS3(preSignedUrl, file);

      setImageInfo({ file, previewURL: uploadedFileUrl });

      await updateMenuThumbnailAsync({
        sid: storeId,
        params: { menuId: id, imageThumbnail: { url: uploadedFileUrl, width: imgWidth, height: imgHeight } }
      });
      await refetchMenuLists();
    } else {
      Modal.error({
        content: '이미지 파일 업로드만 가능합니다.'
      });
    }
  };

  const handleImageDrop = async (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    const file = event.dataTransfer.items[0]?.getAsFile();
    if (!file) return;

    await handleImageUpload(file);
  };

  // 파일 선택창(카드 클릭)으로 고른 이미지 처리
  const handleImageChange = async ({ target }: React.ChangeEvent<HTMLInputElement>) => {
    const file = target.files?.[0];
    // 같은 파일을 다시 골라도 onChange가 발화하도록 값 초기화
    target.value = '';
    if (!file) return;

    await handleImageUpload(file);
  };

  const buttonMouseDown = (event: React.MouseEvent<Element>) => event.stopPropagation();

  return (
    <Col
      ref={setNodeRef}
      span={6}
      style={{
        minWidth: '430px',
        maxWidth: '430px',
        height: '375px',
        transform: CSS.Transform.toString(transform),
        transition,
        zIndex: isDragging ? 999999 : 1
      }}
    >
      <MenuCardWrapper {...listeners} {...attributes}>
        <MenuThumbnail role="presentation" onPointerUp={imageUploadOnClick} onDrop={handleImageDrop}>
          {!imageInfo?.previewURL ? (
            <input type="file" accept={UPLOAD_IMAGE_ACCEPT} ref={inputRef} onChange={handleImageChange} />
          ) : null}
          {!imageInfo?.previewURL ? (
            <div className="non-image-description">
              <div>이미지를 등록해주세요</div>
              <div>드래그 앤 드랍 또는 클릭</div>
              <div className="image-guide-description">(이미지 권장 사이즈 400 x 300)</div>
            </div>
          ) : (
            <img alt="previewImage" className="preview-image" draggable={false} src={imageInfo?.previewURL} />
          )}
        </MenuThumbnail>
        <MenuStatusButtonGroup>
          <div className="menu-plan-name">{name}</div>
          <Space direction="vertical">
            <StyledDropdownButton
              $type="MENU"
              $backgroundColor={menuStatus.backgroundColor}
              $borderColor={menuStatus.borderColor}
            >
              <div onMouseDown={handleStatusDropdownClick} onClick={handleStatusDropdownClick}>
                <Dropdown.Button type="primary" menu={{ items: statusMenuItems }}>
                  {getMenuStatusLabel(menuStatus.status)}
                </Dropdown.Button>
              </div>
            </StyledDropdownButton>
          </Space>
        </MenuStatusButtonGroup>
        <div className="menu-intro">{intro}</div>
        <MenuButtonGroup>
          <Button type="primary" className="edit-button" onClick={handleOpenModal} onMouseDown={buttonMouseDown}>
            수정하기
          </Button>
        </MenuButtonGroup>
      </MenuCardWrapper>
    </Col>
  );
};

export default MenuCard;
